<template>
  <div class="fields-order-field">
    <button type="button" class="order-button" :disabled="!fields.length" @click="show">
      <font-awesome-icon icon="sort" />
      Definir orden
    </button>

    <small v-if="!fields.length" class="order-unavailable">
      El orden no está disponible hasta cargar los campos de la capa.
    </small>
    <small v-else class="order-help">
      Este orden se aplica al popup y al selector de columnas del reporte.
    </small>

    <modal
      name="FieldsOrderFormModal"
      :width="'90%'"
      :max-width="680"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      class="fields-order-vmodal"
      @closed="resetModal"
    >
      <section class="fields-order-modal" aria-labelledby="form-fields-order-title">
        <header class="fields-order-header">
          <div>
            <h2 id="form-fields-order-title">Definir orden de campos</h2>
            <p>Arrastre o use las flechas para definir en qué orden se muestran los campos de esta capa.</p>
          </div>
          <button type="button" class="close-button" title="Cerrar" @click="close">&times;</button>
        </header>

        <div class="fields-order-body">
          <ol class="fields-order-list">
            <li
              v-for="(field, index) in orderedFields"
              :key="field.name"
              :class="{ dragging: draggedIndex === index, hidden: !field.visible }"
              draggable="true"
              @dragstart="startDrag(index, $event)"
              @dragover.prevent
              @drop="dropField(index)"
              @dragend="draggedIndex = null"
            >
              <span class="field-position">{{ index + 1 }}</span>
              <span class="drag-handle" title="Arrastrar para reordenar"><font-awesome-icon icon="sort" /></span>
              <span class="field-name">
                {{ field.alias || field.name }}
                <span v-if="!field.visible" class="hidden-badge">No visible en popup</span>
                <span v-if="field.reporte" class="report-badge">Inicial en reporte</span>
              </span>
              <div class="fields-order-controls">
                <button type="button" title="Subir campo" :disabled="index === 0" @click="moveField(index, -1)">
                  <font-awesome-icon icon="arrow-up" />
                </button>
                <button type="button" title="Bajar campo" :disabled="index === orderedFields.length - 1" @click="moveField(index, 1)">
                  <font-awesome-icon icon="arrow-down" />
                </button>
              </div>
            </li>
          </ol>
          <p class="save-note">Este orden se guardará cuando cree o actualice la capa.</p>
        </div>

        <footer class="fields-order-footer">
          <button type="button" class="button-secondary" @click="close">Cancelar</button>
          <button type="button" class="button-primary" @click="applyOrder">Aplicar orden</button>
        </footer>
      </section>
    </modal>
  </div>
</template>

<script>
export default {
  name: 'FieldsOrderFormModal',
  props: {
    // Las filas vivas de FormularioArcgisPaso2: el modal trabaja sobre una copia
    // y solo emite el nuevo orden al confirmar, para que "Cancelar" no deje rastro.
    fields: { type: Array, default: () => [] }
  },
  data() {
    return {
      orderedFields: [],
      draggedIndex: null
    }
  },
  methods: {
    show() {
      if (!this.fields.length) return
      this.orderedFields = this.fields.map(field => ({ ...field }))
      this.$modal.show('FieldsOrderFormModal')
    },
    resetModal() {
      this.orderedFields = []
      this.draggedIndex = null
    },
    close() {
      this.$modal.hide('FieldsOrderFormModal')
    },
    startDrag(index, event) {
      this.draggedIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(index))
    },
    dropField(targetIndex) {
      if (this.draggedIndex === null || this.draggedIndex === targetIndex) return
      const moved = this.orderedFields.splice(this.draggedIndex, 1)[0]
      this.orderedFields.splice(targetIndex, 0, moved)
      this.draggedIndex = null
    },
    moveField(index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= this.orderedFields.length) return
      const moved = this.orderedFields.splice(index, 1)[0]
      this.orderedFields.splice(targetIndex, 0, moved)
    },
    applyOrder() {
      this.$emit('order-change', this.orderedFields.map(field => field.name))
      this.close()
    }
  }
}
</script>

<style lang="scss" scoped>
.fields-order-field { width: 100%; }
.order-button { display: inline-flex; align-items: center; gap: .45rem; min-height: 36px; padding: .45rem .8rem; border: 1px solid var(--color-navy); border-radius: 4px; background: #fff; color: var(--color-navy); font-weight: 600; cursor: pointer; }
.order-button:hover:not(:disabled) { border-color: var(--color-orange); color: var(--color-orange); }
.order-button:disabled { border-color: #cbd6de; background: #edf1f3; color: #8a98a3; cursor: not-allowed; }
.order-help,.order-unavailable { display: block; margin-top: .35rem; font-size: .76rem; }
.order-help { color: #667786; }
.order-unavailable { color: #922b21; }
.fields-order-vmodal::v-deep .vm--modal { background: transparent; box-shadow: none; }
.fields-order-modal { overflow: hidden; width: 100%; max-height: 88vh; border-radius: 8px; background: #fff; box-shadow: 0 18px 48px rgba(15,42,61,.28); color: var(--color-navy); }
.fields-order-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; padding: 1.25rem 1.4rem; border-bottom: 1px solid #dfe6eb; }
.fields-order-header h2 { margin: 0 0 .35rem; font-size: 1.15rem; }
.fields-order-header p { margin: 0; color: #667786; font-size: .82rem; line-height: 1.45; }
.close-button { flex: 0 0 auto; width: 30px; height: 30px; border: 0; border-radius: 4px; background: transparent; color: #667786; font-size: 1.4rem; cursor: pointer; }
.close-button:hover { background: #edf2f5; color: var(--color-navy); }
.fields-order-body { overflow-y: auto; max-height: 60vh; padding: 1rem 1.4rem; background: #f7f9fa; }
.fields-order-list { display: flex; flex-direction: column; gap: .45rem; margin: 0; padding: 0; list-style: none; }
.fields-order-list li { display: grid; grid-template-columns: 32px 24px minmax(0,1fr) auto; align-items: center; gap: .55rem; min-height: 44px; padding: .45rem .55rem; border: 1px solid #d7e0e6; border-radius: 5px; background: #fff; cursor: grab; }
.fields-order-list li.hidden { opacity: .6; }
.fields-order-list li.dragging { opacity: .5; }
.field-position { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 4px; background: #edf2f5; font-size: .78rem; font-weight: 700; }
.drag-handle { color: #8a9aa7; text-align: center; }
.field-name { overflow: hidden; color: #32485a; font-size: .86rem; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.hidden-badge { display: inline-block; margin-left: .45rem; padding: .12rem .35rem; border-radius: 3px; background: #8a9aa7; color: #fff; font-size: .66rem; vertical-align: middle; }
.report-badge { display: inline-block; margin-left: .35rem; padding: .12rem .35rem; border-radius: 3px; background: #f28c28; color: #fff; font-size: .66rem; vertical-align: middle; }
.fields-order-controls { display: flex; gap: .35rem; }
.fields-order-controls button { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: 1px solid #cbd6de; border-radius: 4px; background: #fff; color: var(--color-navy); cursor: pointer; }
.fields-order-controls button:hover:not(:disabled) { border-color: var(--color-orange); color: var(--color-orange); }
.fields-order-controls button:disabled { opacity: .4; cursor: not-allowed; }
.save-note { margin: .75rem 0 0; color: #667786; font-size: .76rem; text-align: right; }
.fields-order-footer { display: flex; justify-content: flex-end; gap: .6rem; padding: 1rem 1.4rem; border-top: 1px solid #dfe6eb; background: #fff; }
.fields-order-footer button { min-height: 36px; padding: .45rem .9rem; border-radius: 4px; font-size: .82rem; font-weight: 600; cursor: pointer; }
.button-secondary { border: 1px solid #cbd6de; background: #fff; color: var(--color-navy); }
.button-primary { border: 1px solid var(--color-navy); background: var(--color-navy); color: #fff; }
@media (max-width: 520px) { .fields-order-list li { grid-template-columns: 28px 20px minmax(0,1fr); } .fields-order-controls { grid-column: 1 / -1; justify-content: flex-end; } }
</style>
